/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { Overlay, PositionStrategy } from '@angular/cdk/overlay';
import { NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ViewportService } from '@libs/shared/services';
import { OverlayDirective } from '../../../overlays/overlay';

@Component({
  selector: 'ui-image-gallery-magnifier',
  templateUrl: 'image-gallery-magnifier.component.html',
  styleUrl: './image-gallery-magnifier.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OverlayDirective,
    NgOptimizedImage,
  ],
})
export class ImageGalleryMagnifierComponent implements AfterViewInit {
  private readonly document: Document = inject(DOCUMENT);
  private readonly overlay: Overlay = inject(Overlay);
  private readonly viewPortService: ViewportService = inject(ViewportService);

  public readonly src: InputSignal<string> = input.required();
  public readonly zoomImageSrc: InputSignal<string> = input.required();
  public readonly alt: InputSignal<string> = input.required();
  public readonly offsetX: InputSignal<number> = input(0);
  public readonly offsetY: InputSignal<number> = input(0);
  public readonly overlayReferenceElement: InputSignal<HTMLElement | undefined> = input();

  public readonly mainImage: Signal<ElementRef<HTMLImageElement> | undefined> = viewChild<ElementRef<HTMLImageElement>>('mainImage');
  public readonly zoomImageContainer: Signal<ElementRef<HTMLElement> | undefined> = viewChild<ElementRef<HTMLElement>>('zoomImage');
  public readonly zoomOverlay: Signal<OverlayDirective | undefined> = viewChild(OverlayDirective);
  public readonly lens: Signal<ElementRef<HTMLDivElement> | undefined> = viewChild<ElementRef<HTMLDivElement>>('lens');
  public readonly imageContainer: Signal<ElementRef<HTMLDivElement>> = viewChild.required<ElementRef<HTMLDivElement>>('imageRef');

  public readonly lensVisible: WritableSignal<boolean> = signal(false);
  public readonly imageOverlayPositionStrategy: WritableSignal<PositionStrategy | undefined> = signal(undefined);
  public readonly isMobile: Signal<boolean> = toSignal(this.viewPortService.isMobile(), { initialValue: false });

  public ngAfterViewInit(): void {
    const origin: HTMLElement = this.overlayReferenceElement() ?? this.imageContainer().nativeElement;

    this.imageOverlayPositionStrategy.set(this.overlay.position().flexibleConnectedTo(origin).withPositions([
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
        offsetX: this.offsetX(),
        offsetY: this.offsetY(),
      },
    ]));
  }

  public showLens(): void {
    this.lensVisible.set(true);
  }

  public hideLens(): void {
    this.lensVisible.set(false);
  }

  public showZoom(): void {
    if (this.isMobile()) {
      return;
    }

    this.showLens();
    this.zoomOverlay()?.show();
  }

  public hideZoom(): void {
    this.hideLens();
    this.zoomOverlay()?.close();
  }

  public setPositions(event: MouseEvent): void {
    if (!this.lensVisible()) {
      return;
    }

    const lensEl: ElementRef<HTMLDivElement> | undefined = this.lens();
    const mainImageEl: ElementRef<HTMLImageElement> | undefined = this.mainImage();
    const zoomImageContainerEl: ElementRef<HTMLElement> | undefined = this.zoomImageContainer();

    if (!lensEl || !mainImageEl || !zoomImageContainerEl) {
      // Overlay elements can be missing for the first mousemove after hover
      return;
    }

    const lens: HTMLDivElement = lensEl.nativeElement;
    const img: HTMLImageElement = mainImageEl.nativeElement;
    const zoomImg: HTMLElement = zoomImageContainerEl.nativeElement;
    const cx: number = zoomImg.offsetWidth / lens.offsetWidth;
    const cy: number = zoomImg.offsetHeight / lens.offsetHeight;

    zoomImg.style.backgroundSize = (img.width * cx) + 'px ' + (img.height * cy) + 'px';

    const pos: { x: number; y: number } = this.getCursorPos(event);
    const lensHalfWidth: number = (lens.offsetWidth / 2);
    const lensHalfHeight: number = (lens.offsetHeight / 2);

    let x: number = pos.x - lensHalfWidth;
    let y: number = pos.y - lensHalfHeight;

    /* Prevent the lens from being positioned outside the image: */
    if (x > img.width - lens.offsetWidth) {
      x = img.width - lens.offsetWidth;
    }

    if (x < 0) {
      x = 0;
    }

    if (y > img.height - lens.offsetHeight) {
      y = img.height - lens.offsetHeight;
    }

    if (y < 0) {
      y = 0;
    }

    lens.style.top = y + 'px';
    lens.style.left = x + 'px';

    zoomImg.style.backgroundPosition = '-' + (x * cx) + 'px -' + (y * cy) + 'px';
  }

  private getCursorPos(e: MouseEvent): { x: number; y: number } {
    /* Get the x and y positions of the image: */
    const a: DOMRect = this.mainImage()!.nativeElement.getBoundingClientRect();
    let x: number;
    let y: number;

    /* Calculate the cursor's x and y coordinates, relative to the image: */
    x = e.pageX - a.left;
    y = e.pageY - a.top;
    /* Consider any page scrolling: */
    x = x - this.document.defaultView!.scrollX;
    y = y - this.document.defaultView!.scrollY;

    return { x: x, y: y };
  }
}
